import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import JsBarcode from 'jsbarcode';
import { Button, EmptyState, ListPageHeader } from '../components/ui';

// Stoklar tablosundan etikete basılabilen alanlar.
// fiyat1/2/3 yazdırma anında seçilen Fiyat Tanımı'nın price1/2/3'ünden gelir.
export const STOCK_FIELD_LABELS = {
  code: 'Stok Kodu',
  name: 'Stok Adı',
  full_name: 'Tam Ad',
  barcode: 'Barkod',
  fiyat1: 'Fiyat 1',
  fiyat2: 'Fiyat 2',
  fiyat3: 'Fiyat 3',
  fiyat_para_birimi: 'Fiyat Para Birimi',
  vat_rate: 'KDV %',
  currency_code: 'Para Birimi',
  unit_code: 'Birim',
  category_name: 'Kategori',
  brand_name: 'Marka',
  model: 'Model',
  shelf_code: 'Raf Kodu',
  product_code: 'Ürün Kodu',
};

export const STOCK_FIELDS = Object.keys(STOCK_FIELD_LABELS);

// Stok objesinden field değerini al — fiyat alanları sayısal değerleri 2 hane ile basar.
export function getStockValue(stock, field) {
  if (!stock || !field) return '';
  const v = stock[field];
  if (v == null || v === '') return '';
  if (field === 'fiyat1' || field === 'fiyat2' || field === 'fiyat3') {
    const n = parseFloat(v);
    return isNaN(n) ? '' : n.toFixed(2);
  }
  return String(v);
}

// Barkod render — JsBarcode ile SVG
export function BarcodeSVG({ value, format = 'CODE128', showText = true, width, height }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !value) return;
    try {
      JsBarcode(ref.current, String(value), {
        format,
        displayValue: showText,
        width: 1.5,
        height: 40,
        margin: 0,
        fontSize: 12,
      });
    } catch (e) {
      // Geçersiz barkod (örn EAN13 için 12 hane gerekir) — boş bırak
    }
  }, [value, format, showText]);
  return <svg ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

// Tek bir etiketi render et (mm cinsinden)
export function LabelRender({ template, stock, mmToUnit = 'mm' }) {
  const u = mmToUnit; // 'mm' (gerçek baskı) veya 'px' (önizleme — caller scale eder)
  const elements = Array.isArray(template.elements) ? template.elements : [];

  return (
    <div style={{
      position: 'relative',
      width: `${template.label_width}${u}`,
      height: `${template.label_height}${u}`,
      overflow: 'hidden',
      boxSizing: 'border-box',
      background: '#fff',
    }}>
      {elements.map(el => {
        const rot = el.rotation || 0;
        const wrapStyle = {
          position: 'absolute',
          left: `${el.x}${u}`,
          top: `${el.y}${u}`,
          width: `${el.width}${u}`,
          height: `${el.height}${u}`,
          overflow: 'hidden',
          boxSizing: 'border-box',
          ...(rot ? { transform: `rotate(${rot}deg)`, transformOrigin: 'center center' } : {}),
          ...(el.borderWidth ? { border: `${el.borderWidth}px solid ${el.borderColor || '#000'}` } : {}),
        };
        if (el.type === 'field' || el.type === 'text') {
          const raw = el.type === 'text' ? (el.text || '') : getStockValue(stock, el.field);
          const txt = (el.prefix || '') + raw + (el.suffix || '');
          return (
            <div key={el.id} style={{
              ...wrapStyle,
              fontSize: `${el.fontSize || 9}pt`,
              fontFamily: el.fontFamily || 'inherit',
              fontWeight: el.bold ? 700 : 400,
              fontStyle: el.italic ? 'italic' : 'normal',
              textDecoration: el.strike ? 'line-through' : 'none',
              textAlign: el.align || 'left',
              background: el.bgColor || 'transparent',
              display: 'flex',
              alignItems: el.valign === 'middle' ? 'center' : el.valign === 'bottom' ? 'flex-end' : 'flex-start',
              justifyContent: el.align === 'center' ? 'center' : el.align === 'right' ? 'flex-end' : 'flex-start',
              color: el.color || '#000',
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{txt}</span>
            </div>
          );
        }
        if (el.type === 'barcode') {
          const v = getStockValue(stock, el.field || 'barcode');
          return (
            <div key={el.id} style={wrapStyle}>
              <BarcodeSVG value={v} format={el.format || 'CODE128'} showText={el.showText !== false} />
            </div>
          );
        }
        if (el.type === 'image') {
          return (
            <div key={el.id} style={wrapStyle}>
              {el.src && <img src={el.src} alt="" style={{ width: '100%', height: '100%', objectFit: el.fit || 'contain' }} />}
            </div>
          );
        }
        if (el.type === 'rect') {
          return (
            <div key={el.id} style={{
              ...wrapStyle,
              border: `${el.borderWidth || 1}px solid ${el.borderColor || '#000'}`,
              background: el.fill || 'transparent',
            }} />
          );
        }
        return null;
      })}
    </div>
  );
}

export default function StockLabelsPage({ onNavigate }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [priceDefs, setPriceDefs] = useState([]);
  const [priceDefId, setPriceDefId] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [stocks, setStocks] = useState([]);
  const [picked, setPicked] = useState([]); // { stock, qty }
  const [showPreview, setShowPreview] = useState(false);
  const [loadingStocks, setLoadingStocks] = useState(false);
  const [mobileTab, setMobileTab] = useState('list'); // 'list' | 'selected'

  useEffect(() => {
    api.get('/label-templates').then(r => {
      if (r.data.success) {
        const list = r.data.data || [];
        setTemplates(list);
        const def = list.find(t => t.is_default) || list[0];
        if (def) setTemplateId(def.id);
      }
    }).catch(() => {});
    api.get('/price-definitions').then(r => {
      if (r.data?.success) {
        const list = (r.data.data || []).filter(d => d.is_active);
        setPriceDefs(list);
        const def = list.find(d => d.is_preferred) || list[0];
        if (def) setPriceDefId(def.id);
      }
    }).catch(() => {});
  }, []);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchStocks = useCallback(async () => {
    setLoadingStocks(true);
    try {
      const params = { search, limit: 100, page: 1 };
      if (priceDefId) params.price_definition_id = priceDefId;
      const r = await api.get('/stocks', { params });
      if (r.data.success) {
        const items = r.data.data?.items || r.data.data || [];
        setStocks(items);
        // Seçili stokların fiyat tanımı değişince fiyat alanlarını yeniden senkronize et
        setPicked(prev => prev.map(p => {
          const fresh = items.find(it => it.id === p.stock.id);
          return fresh ? { ...p, stock: { ...p.stock, ...fresh } } : p;
        }));
      }
    } catch {} finally { setLoadingStocks(false); }
  }, [search, priceDefId]);

  useEffect(() => { fetchStocks(); }, [fetchStocks]);

  // Fiyat tanımı veya picked listesi değişince, picked stoklar için fiyatları
  // her zaman senkronize et (arama sonuçlarında olmayan stoklar dahil).
  useEffect(() => {
    if (!priceDefId || picked.length === 0) return;
    let cancelled = false;
    const ids = picked.map(p => p.stock.id);
    Promise.all(ids.map(id =>
      api.get('/stocks/' + id + '/prices')
        .then(r => ({ id, items: r.data?.success ? (r.data.data || []) : [] }))
        .catch(() => ({ id, items: [] }))
    )).then(results => {
      if (cancelled) return;
      const byId = {};
      results.forEach(r => { byId[r.id] = r.items; });
      setPicked(prev => prev.map(p => {
        const items = byId[p.stock.id];
        if (!items) return p;
        const m = items.find(d => d.price_definition_id === priceDefId);
        return {
          ...p,
          stock: {
            ...p.stock,
            fiyat1: m?.price1 ?? 0,
            fiyat2: m?.price2 ?? 0,
            fiyat3: m?.price3 ?? 0,
            fiyat_kdv_dahil: m?.vat_included ? 1 : 0,
            fiyat_para_birimi: m?.item_currency_code || m?.def_currency_code || p.stock.currency_code || 'TRY',
          },
        };
      }));
    });
    return () => { cancelled = true; };
  }, [priceDefId, picked.length]);

  const isPicked = (id) => picked.some(p => p.stock.id === id);
  const toggleStock = (s) => {
    setPicked(prev => {
      if (prev.some(p => p.stock.id === s.id)) return prev.filter(p => p.stock.id !== s.id);
      return [...prev, { stock: s, qty: 1 }];
    });
  };
  const updateQty = (id, qty) => {
    setPicked(prev => prev.map(p => p.stock.id === id ? { ...p, qty: Math.max(0, parseInt(qty) || 0) } : p));
  };

  // Şablonun JSON elements'ini parse et
  const selectedTemplate = (() => {
    const t = templates.find(x => x.id === templateId);
    if (!t) return null;
    let els = t.elements;
    if (typeof els === 'string') { try { els = JSON.parse(els); } catch { els = []; } }
    return { ...t, elements: Array.isArray(els) ? els : [] };
  })();

  const totalLabels = picked.reduce((s, p) => s + (p.qty || 0), 0);

  const handlePrint = () => {
    if (!selectedTemplate) return alert('Önce şablon seçin');
    if (totalLabels === 0) return alert('En az 1 etiket seçin');
    setShowPreview(true);
  };

  const doPrint = () => {
    setTimeout(() => window.print(), 100);
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Barkod Etiket Dökümü"
        count={totalLabels}
        unit="etiket"
        onRefresh={fetchStocks}
        refreshing={loadingStocks}
        actions={<>
          <Button variant="ghost" size="sm" onClick={() => onNavigate && onNavigate('label-templates')}>Şablonları Yönet</Button>
          <Button size="sm" onClick={handlePrint} disabled={!totalLabels}>🖨 Yazdır</Button>
        </>}
      />

      {/* Mobil sekmeler — Stoklar / Seçili */}
      <div className="label-mobile-tabs mobile-only" style={{ display: 'flex', gap: 4, padding: '8px 16px 0', borderBottom: '1px solid var(--border-secondary)' }}>
        <button
          onClick={() => setMobileTab('list')}
          style={{
            flex: 1, padding: '10px', background: 'none', border: 'none',
            borderBottom: mobileTab === 'list' ? '2px solid var(--accent-primary)' : '2px solid transparent',
            color: mobileTab === 'list' ? 'var(--accent-primary)' : 'var(--text-secondary)',
            fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
          }}
        >Stoklar</button>
        <button
          onClick={() => setMobileTab('selected')}
          style={{
            flex: 1, padding: '10px', background: 'none', border: 'none',
            borderBottom: mobileTab === 'selected' ? '2px solid var(--accent-primary)' : '2px solid transparent',
            color: mobileTab === 'selected' ? 'var(--accent-primary)' : 'var(--text-secondary)',
            fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
          }}
        >Seçili ({picked.length})</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, padding: 16 }}>
        {/* SOL: Stok arama + liste */}
        <div className={mobileTab === 'selected' ? 'mobile-hidden' : ''}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select
              value={templateId}
              onChange={e => setTemplateId(e.target.value)}
              style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
            >
              <option value="">— Şablon seç —</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <select
              value={priceDefId}
              onChange={e => setPriceDefId(e.target.value)}
              title="Etikete basılacak fiyat tanımı (price1 → Fiyat 1, price2 → Fiyat 2, price3 → Fiyat 3)"
              style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
            >
              <option value="">— Aktarılacak fiyat —</option>
              {priceDefs.map(d => (
                <option key={d.id} value={d.id}>{d.name}{d.price_group ? ' (' + d.price_group + ')' : ''}</option>
              ))}
            </select>
          </div>
          <input
            type="text"
            placeholder="Stok ara (kod, ad, barkod)..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', boxSizing: 'border-box', marginBottom: 8 }}
          />
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 10, maxHeight: 480, overflowY: 'auto' }}>
            {loadingStocks && <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>}
            {!loadingStocks && stocks.length === 0 && <EmptyState title="Stok bulunamadı" compact />}
            {stocks.map(s => {
              const checked = isPicked(s.id);
              return (
                <div
                  key={s.id}
                  onClick={() => toggleStock(s)}
                  style={{
                    padding: '8px 12px',
                    borderBottom: '1px solid var(--border-secondary)',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: checked ? 'rgba(99,102,241,0.08)' : 'transparent',
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => {}} style={{ pointerEvents: 'none' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <span style={{ color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{s.code}</span> · {s.name}
                    </div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)' }}>
                      {s.barcode ? '🎫 ' + s.barcode : 'Barkod yok'}
                      {s.fiyat1 ? ' · ₺' + parseFloat(s.fiyat1).toFixed(2) : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* SAĞ: Seçili stoklar + adetler */}
        <div className={mobileTab === 'list' ? 'mobile-hidden' : ''}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0 0 8px' }}>Seçili Stoklar ({picked.length})</h3>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 10, maxHeight: 540, overflowY: 'auto' }}>
            {picked.length === 0 && <EmptyState title="Soldan stok seçin" compact />}
            {picked.map(p => {
              const cur = p.stock.fiyat_para_birimi || p.stock.currency_code || 'TRY';
              const sym = cur === 'TRY' ? '₺' : cur === 'USD' ? '$' : cur === 'EUR' ? '€' : (cur + ' ');
              const fmt = (v) => {
                const n = parseFloat(v);
                return (!isNaN(n) && n > 0) ? sym + n.toFixed(2) : '—';
              };
              return (
                <div key={p.stock.id} style={{ padding: 10, borderBottom: '1px solid var(--border-secondary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.stock.name}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>{p.stock.code}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span><span style={{ color: 'var(--text-tertiary)' }}>F1:</span> {fmt(p.stock.fiyat1)}</span>
                      <span><span style={{ color: 'var(--text-tertiary)' }}>F2:</span> {fmt(p.stock.fiyat2)}</span>
                      <span><span style={{ color: 'var(--text-tertiary)' }}>F3:</span> {fmt(p.stock.fiyat3)}</span>
                    </div>
                  </div>
                  <input
                    type="number"
                    min={0}
                    value={p.qty}
                    onChange={e => updateQty(p.stock.id, e.target.value)}
                    style={{ width: 60, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', textAlign: 'center' }}
                  />
                  <button
                    onClick={() => toggleStock(p.stock)}
                    title="Kaldır"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 4 }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Yazdırma önizleme — fullscreen modal, @media print sadece bunu basar */}
      {showPreview && selectedTemplate && (
        <PrintPreview
          template={selectedTemplate}
          picked={picked}
          onClose={() => setShowPreview(false)}
          onPrint={doPrint}
        />
      )}
    </div>
  );
}

function PrintPreview({ template, picked, onClose, onPrint }) {
  // Tüm etiketleri tek diziye aç (her stok × adet)
  const allLabels = [];
  picked.forEach(p => {
    for (let i = 0; i < (p.qty || 0); i++) allLabels.push(p.stock);
  });

  const labelsPerPage = template.cols * template.rows;
  const pages = [];
  for (let i = 0; i < allLabels.length; i += labelsPerPage) {
    pages.push(allLabels.slice(i, i + labelsPerPage));
  }
  // Eğer hiç sayfa yoksa boş bir sayfa göster
  if (pages.length === 0) pages.push([]);

  // @page CSS injection: kağıt boyutuna uyacak
  const pageCss = `
    @page { size: ${template.paper_width}mm ${template.paper_height}mm; margin: 0; }
    @media print {
      html, body { margin: 0; padding: 0; background: #fff; }
      body * { visibility: hidden; }
      .label-print-area, .label-print-area * { visibility: visible; }
      .label-print-area { position: absolute; left: 0; top: 0; width: 100%; }
      .label-print-page { page-break-after: always; }
      .label-print-page:last-child { page-break-after: auto; }
      .label-print-toolbar { display: none !important; }
    }
  `;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: '#444',
      overflow: 'auto',
    }}>
      <style>{pageCss}</style>
      <div className="label-print-toolbar" style={{
        position: 'sticky', top: 0, zIndex: 5,
        background: 'var(--bg-card)', borderBottom: '1px solid var(--border-secondary)',
        padding: 10, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <strong style={{ flex: 1 }}>Önizleme — {template.name} · {allLabels.length} etiket / {pages.length} sayfa</strong>
        <Button variant="ghost" size="sm" onClick={onClose}>Kapat</Button>
        <Button size="sm" onClick={onPrint}>🖨 Yazdır</Button>
      </div>

      <div className="label-print-area" style={{ padding: 20 }}>
        {pages.map((pageItems, pageIdx) => (
          <div
            key={pageIdx}
            className="label-print-page"
            style={{
              width: `${template.paper_width}mm`,
              height: `${template.paper_height}mm`,
              background: '#fff',
              margin: '0 auto 12px',
              padding: `${template.margin_top}mm ${template.margin_right}mm ${template.margin_bottom}mm ${template.margin_left}mm`,
              boxSizing: 'border-box',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              position: 'relative',
            }}
          >
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${template.cols}, ${template.label_width}mm)`,
              gridAutoRows: `${template.label_height}mm`,
              columnGap: `${template.gap_x}mm`,
              rowGap: `${template.gap_y}mm`,
            }}>
              {Array.from({ length: template.cols * template.rows }).map((_, idx) => {
                const stock = pageItems[idx];
                if (!stock) return <div key={idx} />;
                return (
                  <div key={idx} style={{ width: `${template.label_width}mm`, height: `${template.label_height}mm`, overflow: 'hidden' }}>
                    <LabelRender template={template} stock={stock} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
